import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addHoliday,
  getHolidays,
  deleteHoliday,
  updateHoliday,
} from "../redux/slices/holidaySlice";
import "../assets/Holiday.css";
import { toast } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";

export default function HolidayManager() {
  const dispatch = useDispatch();
  const { holidays } = useSelector((state) => state.holiday);

  const [form, setForm] = useState({
    name: "",
    date: "",
    type: "National Holiday",
    region: "",
    isRecurring: false,
    description: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null); // for confirmation modal

  useEffect(() => {
    dispatch(getHolidays());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const superAdminId = localStorage.getItem("adminuserId"); // ✅ read from localStorage
    if (!superAdminId) {
      toast.error("❌ Super Admin ID not found in localStorage");
      return;
    }

    const payload = {
      superAdminId,
      name: form.name,
      date: form.date,
      type: form.type,
      region: form.region || "NA",
      isRecurring: form.isRecurring,
      description: form.description,
    };

    if (editingId) {
      const result = await dispatch(updateHoliday({ id: editingId, data: payload }));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("✅ Holiday updated successfully!");
      }
    } else {
      const result = await dispatch(addHoliday(payload));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("✅ Holiday added successfully!");
      }
    }

    dispatch(getHolidays());
    setForm({
      name: "",
      date: "",
      type: "National Holiday",
      region: "",
      isRecurring: false,
      description: "",
    });
    setEditingId(null);
  };

  const handleDelete = (id) => {
    setDeleteId(id); // open modal
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      const result = await dispatch(deleteHoliday(deleteId));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("🗑️ Holiday deleted successfully!");
      }
      dispatch(getHolidays());
      setDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteId(null);
  };

  const handleEdit = (holiday) => {
    setEditingId(holiday.id);
    setForm({
      name: holiday.name,
      date: holiday.date.split("T")[0],
      type: holiday.type,
      region: holiday.region === "NA" ? "" : holiday.region,
      isRecurring: holiday.isRecurring,
      description: holiday.description || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({
      name: "",
      date: "",
      type: "National Holiday",
      region: "",
      isRecurring: false,
      description: "",
    });
  };

  return (
    <div className="Holiday-form-admin-container">
      {/* === Form Section === */}
      <section className="Holiday-form-admin-section">
        <form onSubmit={handleSubmit} className="Holiday-form-admin-form">
          <h2 className="Holiday-form-admin-header">
            {editingId ? "✏️ Edit Holiday" : "➕ Add Holiday"}
          </h2>

          <div className="Holiday-form-admin-row">
            <div className="Holiday-form-admin-field">
              <label>Holiday Name</label>
              <input
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
              />
            </div>
            <div className="Holiday-form-admin-field">
              <label>Date</label>
              <input
                name="date"
                type="date"
                required
                value={form.date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="Holiday-form-admin-row">
            <div className="Holiday-form-admin-field">
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                <option value="National Holiday">National Holiday</option>
                <option value="Regional Holiday">Regional Holiday</option>
                <option value="Optional Holiday">Optional Holiday</option>
              </select>
            </div>
            <div className="Holiday-form-admin-field">
              <label>Region (optional)</label>
              <input
                name="region"
                type="text"
                value={form.region}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="Holiday-form-admin-checkbox">
            <input
              name="isRecurring"
              type="checkbox"
              checked={form.isRecurring}
              onChange={handleChange}
            />
            <label>Is Recurring?</label>
          </div>

          <div className="Holiday-form-admin-field">
            <label>Description</label>
            <textarea
              name="description"
              rows="3"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          <div className="Holiday-form-admin-actions">
            <button type="submit" className="Holiday-form-admin-btn-primary">
              {editingId ? "Update Holiday" : "Add Holiday"}
            </button>
            {editingId && (
              <button
                type="button"
                className="Holiday-form-admin-btn-cancel"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      {/* === Table Section === */}
      <section className="Holiday-form-admin-table-section">
        <h2 className="Holiday-form-admin-subheader">📅 Holiday Report</h2>

        <div className="Holiday-form-admin-table-wrapper">
          <table className="Holiday-form-admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Date</th>
                <th>Type</th>
                <th>Region</th>
                <th>Recurring</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays?.map((holiday, index) => (
                <tr key={holiday.id}>
                  <td>{index + 1}</td>
                  <td>{holiday.name}</td>
                  <td>{holiday.date?.split("T")[0]}</td>
                  <td>{holiday.type}</td>
                  <td>{holiday.region}</td>
                  <td>{holiday.isRecurring ? "Yes" : "No"}</td>
                  <td className="Holiday-form-admin-action-buttons">
                    <button
                      onClick={() => handleEdit(holiday)}
                      className="Holiday-form-admin-btn-edit"
                    >
                     <FaEdit/>
                    </button>
                    <button
                      onClick={() => handleDelete(holiday.id)}
                      className="Holiday-form-admin-btn-delete"
                    >
                      <FaTrash/>
                    </button>
                  </td>
                </tr>
              ))}
              {holidays?.length === 0 && (
                <tr>
                  <td colSpan="7" className="Holiday-form-admin-empty">
                    No holidays found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* === Delete Confirmation Modal === */}
      {deleteId && (
        <div className="Holiday-form-admin-confirm-overlay">
          <div className="Holiday-form-admin-confirm-box">
            <p>⚠️ Are you sure you want to delete this holiday?</p>
            <div className="Holiday-form-admin-confirm-actions">
              <button
                className="Holiday-form-admin-btn-delete"
                onClick={handleConfirmDelete}
              >
                Yes, Delete
              </button>
              <button
                className="Holiday-form-admin-btn-cancel"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
